import { html } from '@microsoft/fast-element';

export const SellerDashboardKnowledgeBlock = html`
  <div class="knowledge-block-modal-content">
    <h1 class="title ">Overview</h1>
    <p>
      The Dashboard page provides an overview of all activity that has been undertaken by a Seller
      Company and functionality for the management of Project Listing, Contract (Offer) listing and
      management, monitoring and tracking of trading activity.
    </p>

    <h1 class="title ">Seller Dashboard</h1>

    <p>
      The Seller Dashboard allows for the effective monitoring of trading activity through the key
      stages of the end-to-end workflow captured in the following tabs: Trading Activity, Active
      Contracts, Inactive Contracts, Negotiations, Executed and Terminated.
    </p>
    <p>
      Hovering over the ? icon on each tab will provide a brief description of the data captured in
      each tab.
    </p>

    <p>
      Selection of a Seller Dashboard tab will display any relevant corresponding activity for that
      tab.
    </p>

    <p class="item">Contract (Offer) Management and Maintenance</p>

    <p>
      Contracts (Offers) can be added (listed) using the + New Contract button located in the top
      right corner of the panel which will light up when a Project is selected in the My Projects
      panel. Alternatively, the Add New Contract link located in the center of the Dashboard tabs
      can be used for Projects that have do not have any Contracts added.
    </p>
    <p>
      Listed Contracts (Offers) can be modified, delisted, or deleted as required using the
      appropriate buttons in the Actions column in the Seller’s Dashboard for the relevant Contract
      (Offer) record.
    </p>
    <p>
      Contracts (Offers) cannot be deleted if they are under negotiation in the Engagements Module
      or Contracting Module. To delete such a Contract (Offer) record, all negotiations associated
      with that Contract (Offer) in the Engagements Module need be to be rejected, and all
      negotiations in the Contracting module need to terminated before such deletion can occur.
    </p>
    <p class="item">Action Buttons and Hyperlinks</p>
    <p>
      Using the Action Button (indicating the current Engagement status) in the Trading Activity tab
      will take the user to the Active Engagement negotiation for the selected negotiation in the
      Engagements Module.
    </p>
    <p>
      Using the Action Button (indicating the current Negotiation status) in the Negotiations tab
      will open the Contracting Module or Execution modal (as appropriate) for the selected
      negotiation.
    </p>
    <p>
      Using the Action Button (indicating the Transaction Status) in the Executed tab will open the
      Contracting Module for those Transactions subject to the RFQ Method of Execution where the
      Executed Transaction Confirmation can be viewed. For the Auto Execution Method, the Execution
      Modal will open displaying the Execution Summary.
    </p>
    <p>
      Using the Download button located in the top right corner will export the Contract (Offer)
      records displayed in the UI for the selected Dashboard tab. Each Dashboard tab will need to be
      downloaded individually.
    </p>

    <p>
      Selecting the Project Name hyperlink for a Contract (Offer) listed in the dashboard will
      display the Project Overview and Project Metrics page where financial and carbon analysis can
      be undertaken.
    </p>

    <h1 class="title">My Projects</h1>

    <p>
      My Projects displays an overview of all listed Projects and their associated Contract (Offers)
      on the Projects tab.
    </p>

    <p>
      Listed Projects can be modified as required using the Edit Project button located in the top
      right corner of the Project Statistics panel, next to the My Projects heading.
    </p>

    <p>
      An overview of all Draft Projects is displayed on the Draft Projects tab. The Draft Project
      functionality offers the opportunity for Projects to be saved in draft state, allowing for
      updates to be made as Project details are finalized, thus ensuring that data is accurate and
      complete prior to adding and listing the Project on CleanTrade. Only Draft Projects can be
      deleted.
    </p>

    <p>
      Selecting the Project Name or a Contract’s (Offer’s) Unique ID hyperlink will display the
      Project Overview and Project Metrics page where financial and carbon analysis can be
      undertaken.
    </p>
    <p>
      Selecting a Project record in My Projects filters the view on the Dashboard, displaying the
      Individual Project Statistics and Contracts (Offers) and activity associated with the selected
      Project.
    </p>

    <h1 class="title">Project Statistics</h1>
    <p class="item">All Project Statistics</p>
    <p>
      This is the default Project Statistics view and displays all activity associated with all
      listed Projects for the Seller’s Company and any Project associated with the Seller’s Company
      (Parent entity) that is a Special Purpose Vehicle (Child entity).
    </p>

    <ul>
      <li>
        <p>
          Progress Bar: Displays the volume of all Executed Contracts (Offers) as a percentage of
          the Seller’s Total Available Capacity. This shows the Seller’s progress in contracting all
          capacity that is available for trading across all Projects on CleanTrade, calculated as
          follows: (Total Executed Capacity / Total Available Capacity) * 100
        </p>
      </li>
      <li>
        <p>
          Total Available Capacity (MW): Displays the total capacity (in MW) across all Projects, as
          entered in the Available Capacity field for each of the Seller’s Projects, calculated as
          the sum of all the Available Capacity values across all the Seller’s projects.
        </p>
      </li>
      <li>
        <p>
          Total Tradable Capacity (MW): Displays the total remaining capacity (in MW) across all
          Projects available for trading, calculated by subtracting the Total Executed Capacity (MW)
          value from the Total Available Capacity (MW) value.
        </p>
      </li>
      <li>
        <p>
          Total Executed Capacity (MW): Displays the total capacity (in MW) of all Contracts (Offers)
          successfully executed across all Projects, calculated as the sum of all Offered Capacity
          values for each successfully executed Contract across all the Seller’s Projects.
        </p>
      </li>
    </ul>

    <p class="item">Individual Project Statistics</p>

    <p>
      Displayed when an individual (listed) Project is selected in the My Projects panel. All values
      (e.g., Progress Bar, Available Capacity (MW), etc.) maintain the same logic of All Project
      Statistics, but are derived only from the Project that is actively selected.
    </p>

    <h1 class="title ">Market Index Performance Chart</h1>

    <p>
      The Market Index Performance Chart is in the lower left quadrant of the Dashboard page and
      displays Historical Market Performance data including hub prices, generation-weighted
      performance, and carbon metrics.
    </p>

    <p>
      The chart displays the first/ top Contract (Offer) record in the Recent Activity tab by
      default.
    </p>
    <p>
      If a different Contract (Offer) record is selected in the Trading Activity, Active Contracts
      or Inactive Contracts tabs of the Seller Dashboard, or a different Contract (Offer) record is
      selected under a Project in My Projects, then the chart will display the data associated with
      the selected Contract (Offer) record unless the Contract has a status of Awaiting Key Metrics,
      which indicates that Key Metrics are not yet available for the Contract in question.
    </p>

    <p>
      Hovering over the graph will display a tooltip with the relevant metrics data for that point
      in time.
    </p>
  </div>
`;
